import { FC, useEffect } from 'react'
import { useTranslation } from 'next-i18next'
import { Button } from '@corratech/pylot-ui'
import { useUI, ToastType } from '@corratech/pylot-ui/context'
import { useForm } from 'react-hook-form'
import { DeleteWishlist } from '@corratech/pylot-wishlist/src/DeleteWishlist'
import { Wishlist, WishlistItemUpdateInput } from '@pylot-data/pylotschema'
import { WishlistVisibilityEnum } from '@pylot-data/enums/WishlistVisibilityEnum.d'

import { WishlistCount } from '@corratech/pylot-wishlist/src/WishlistItems'
import { WishlistItems } from '../WishlistItems/WishlistItems'
import { useWishlistManager } from '@corratech/pylot-wishlist/src/Hooks/useWishlistManager'
import {
    WishlistToolbar,
    AddProductsToCart
} from '@corratech/pylot-wishlist/src/WishlistToolbar'
import { useUpdateProductsInWishlist } from '@corratech/pylot-wishlist/src/Hooks/use-update-products-in-wishlist'
import { UpdateWishlist } from '@corratech/pylot-wishlist/src/CreateWishlist'
import { useStoreConfig } from '@config/hooks/useStoreConfig'
import { Lock } from 'react-feather'
import s from '@pagestyles/Wishlist.module.scss'

export type Props = {
    wishlist: Wishlist
}

export const WishlistContent: FC<Props> = ({ wishlist }) => {
    const { t } = useTranslation(['wishlist'])
    const { openToast } = useUI()
    const {
        register,
        handleSubmit,
        getValues,
        unregister,
        reset,
        formState: { errors }
    } = useForm<Array<WishlistItemUpdateInput>>({ mode: 'onChange' })
    const {
        isLoading,
        updateProductsInWishlist
    } = useUpdateProductsInWishlist()
    const wishlistManager = useWishlistManager()
    const { resetSelected } = wishlistManager
    const hasWishlistItem = wishlist?.items_count! > 0
    const config = useStoreConfig()
    //Reset selected items on page transition
    useEffect(() => {
        reset()
        unregister()
        resetSelected()
    }, [wishlist?.id])

    const onSubmit = async (formInput: Array<WishlistItemUpdateInput>) => {
        try {
            const response = await updateProductsInWishlist({
                wishlistId: String(wishlist?.id),
                wishlistItems: formInput
            })

            if (response?.errors?.length) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }

            if (response?.data?.updateProductsInWishlist?.wishlist) {
                openToast(
                    t('wishlist|Wish list has been Updated'),
                    ToastType.Success
                )
            }
        } catch (e) {
            if (e) {
                openToast(
                    t('wishlist|Something went wrong. Please try again'),
                    ToastType.Danger
                )
            }
        }
    }

    if (!wishlist) return null

    return (
        <div>
            <div className={s['wishlist-content-head']}>
                <h2 className={s['wishlist-content-title']}>
                    {t(`wishlist|${wishlist?.name}`)}
                </h2>
                <UpdateWishlist wishlist={wishlist} />
            </div>

            <div className={s['wishlist-action-grid']}>
                <div className={s['wishlist-info']}>
                    {config.base.wishlist.multiple_wishlists && (
                        <>
                            {wishlist?.visibility ===
                                WishlistVisibilityEnum.Public && (
                                <span className={s.public}>
                                    {t('wishlist|Public')}
                                </span>
                            )}
                            {wishlist?.visibility ===
                                WishlistVisibilityEnum.Private && (
                                <span className={s.private}>
                                    <Lock width="15px" height="15px" />
                                    {t('wishlist|Private')}
                                </span>
                            )}
                        </>
                    )}
                    <WishlistCount wishlist={wishlist} />
                </div>
                <WishlistToolbar
                    wishlist={wishlist}
                    wishlistManager={wishlistManager}
                />
            </div>
            <WishlistItems
                wishlist={wishlist}
                wishlistManager={wishlistManager}
                register={register}
                getValues={getValues}
                errors={errors}
            />

            <div className={s['wishlist-action-bottom']}>
                {hasWishlistItem && (
                    <>
                        <Button
                            loading={isLoading}
                            disabled={isLoading}
                            variant="secondary"
                            className="btn-sm"
                            onClick={handleSubmit(onSubmit)}
                        >
                            {t('wishlist|Update Wish List')}
                        </Button>
                        <AddProductsToCart
                            wishlist={wishlist}
                            getValues={getValues}
                            buttonLabel={t('wishlist|Add All to Cart')}
                        />
                    </>
                )}
                <DeleteWishlist wishlist={wishlist} />
            </div>
        </div>
    )
}
